import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Image from 'next/image'
import {useLinkResolver} from 'prismicio'
import type {CtaV2SliceFacepile} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import type {Context} from 'src/common/types'

type FacepileVariationProps = {
  slice: CtaV2SliceFacepile & {
    slice_type: string
    variation: string
  }
  index: number
  context: Context
}

export const FacepileVariation = ({
  slice,
  index: componentIndex,
  context: {pageUid, buttonsData},
}: FacepileVariationProps) => {
  const {linkResolver} = useLinkResolver()

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.primary_button,
  })

  const backgroundColor = slice.primary.background_color

  const titleColor = backgroundColor === 'Dark green' ? 'text-moss-white' : 'text-moss-black'
  const descriptionColor = backgroundColor === 'Dark green' ? 'text-moss-white/70' : 'text-moss-grey-550'

  return (
    <Container
      backgroundColor="Grey/100"
      className={cn('px-5 md:px-10 lg:px-5 xl:px-0', {
        '!py-0': slice.primary.remove_outer_padding,
        '!py-5 md:!py-10': !slice.primary.remove_outer_padding,
      })}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <div
        className={cn('w-full max-w-[1200px] overflow-hidden rounded-lg', {
          'bg-moss-grey-100': slice.primary.background_color === 'Grey/100',
          'bg-green-dark': slice.primary.background_color === 'Dark green',
        })}
      >
        <div className="flex w-full flex-col items-center justify-center gap-6 p-6 md:py-8 md:max-lg:gap-4 lg:py-12">
          {slice.primary.faces && slice.primary.faces.length > 0 && (
            <div className="isolate flex items-center pb-1 pl-0 pr-2.5 pt-0">
              {slice.primary.faces.slice(0, 3).map((face, index) => (
                <div
                  key={index}
                  className="relative -mr-2.5 size-10 shrink-0 overflow-hidden rounded-full border border-solid border-moss-white bg-moss-grey-200"
                  style={{zIndex: 3 - index}}
                >
                  {isFilled.image(face.portrait) && (
                    <Image
                      src={face.portrait.url || ''}
                      alt={face.portrait.alt || 'Face'}
                      fill
                      className="object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Title and Description */}
          <div className="flex w-full max-w-[584px] flex-col items-center justify-center gap-2 text-center">
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading2: ({children}) => (
                  <h2 className={cn('text-24 font-medium leading-normal', titleColor)}>{children}</h2>
                ),
                em: () => <br className="md:hidden" />,
              }}
            />
            <PrismicRichText
              field={slice.primary.description}
              components={{
                paragraph: ({children}) => <p className={cn('text-tg-body-l', descriptionColor)}>{children}</p>,
                em: () => <br className="md:hidden" />,
              }}
            />
          </div>

          {primaryButtonData && (
            <ButtonV2
              variant="primary"
              trackingData={{
                mktgId: primaryButtonData.marketing_id || 'cta-button',
                location: `component-${componentIndex}-cta-facepile`,
                dataType: 'primary-button',
                pageName: pageUid,
              }}
              href={linkResolver(primaryButtonData.link)}
              className="h-12 w-fit"
              border={!slice.primary.hide_button_border}
            >
              {primaryButtonData.label}
            </ButtonV2>
          )}
        </div>
      </div>
    </Container>
  )
}
